import { useState, useEffect } from 'react';
import { useAuthGuard } from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import client from '../api/client';
import { getProducts, createProduct } from '../api/shopping';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

export default function Shopping() {
  const checkAuth = useAuthGuard();
  const [products, setProducts] = useState<any[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [keywords, setKeywords] = useState<string[]>(['', '', '', '', '']);
  const [results, setResults] = useState<any[]>([]);
  const [savedKeywords, setSavedKeywords] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState<'list' | 'add' | 'detail'>('list');
  const [newUrl, setNewUrl] = useState('');
  const [error, setError] = useState('');
  const [rateLimitModal, setRateLimitModal] = useState(false);
  const [adding, setAdding] = useState(false);

  useEffect(() => { loadProducts(); }, []);

  const loadProducts = async () => {
    try {
      const res = await getProducts();
      setProducts(res.data.products);
    } catch {}
  };

  const loadSavedKeywords = async (productId: number) => {
    try {
      const res = await client.get(`/shopping/keywords/${productId}`);
      setSavedKeywords(res.data.keywords);
    } catch {}
  };

  const handleAddProduct = async () => {
    if (!newUrl) return setError('URL을 입력해주세요');
    setAdding(true); setError('');
    try {
      await createProduct('', newUrl);
      setNewUrl('');
      await loadProducts();
      setView('list');
    } catch (e: any) {
      setError(e.response?.data?.detail || '등록 실패');
    } finally { setAdding(false); }
  };

  const handleSelectProduct = async (p: any) => {
    setSelectedProduct(p);
    setKeywords(['', '', '', '', '']);
    setResults([]);
    setSavedKeywords([]);
    setView('detail');
    try {
      const res = await import('../api/client').then(m => m.default.get(`/shopping/keywords/${p.id}`));
      const saved = res.data.keywords || [];
      if (saved.length > 0) {
        const kws = saved.map((k: any) => k.keyword);
        const padded = [...kws, ...Array(Math.max(0, 5 - kws.length)).fill('')];
        setKeywords(padded);
        setSavedKeywords(saved);
      }
    } catch { loadSavedKeywords(p.id); }
  };

  const handleToggleAutoTrack = async (productId: number) => {
    try {
      await client.post(`/tracking/product/${productId}/auto-track`);
      await loadProducts();
    } catch (err: any) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || '';
      if (status === 429) {
        if (window.confirm(detail + '\n\n업그레이드하시겠습니까?')) {
          window.location.href = '/pricing';
        }
      } else {
        alert('변경 실패');
      }
    }
  };

  const handleDeleteProduct = async (productId: number) => {
    if (!window.confirm('정말 삭제하시겠어요?')) return;
    try {
      await client.delete(`/tracking/product/${productId}`);
      await loadProducts();
    } catch { alert('삭제 실패'); }
  };

  const doCheck = async (keywordsToCheck: string[], append: boolean = false) => {
    const valid = keywordsToCheck.filter(k => k.trim());
    if (!valid.length) return;
    setLoading(true); setError('');

    if (!append) {
      setResults(valid.map(k => ({ keyword: k, status: 'loading' })));
    } else {
      setResults(prev => {
        const existing = prev.filter(r => !valid.includes(r.keyword));
        return [...existing, ...valid.map(k => ({ keyword: k, status: 'loading' }))];
      });
    }

    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/shopping/rank', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ keywords: valid, url: selectedProduct.url, product_id: selectedProduct.id })
      });
      if (res.status === 429) {
        const errData = await res.json();
        const detail = errData.detail || '조회 횟수 초과';
        setResults(valid.map(k => ({ keyword: k, status: 'error' })));
        if (window.confirm(detail + '\n\n업그레이드하시겠습니까?')) {
          window.location.href = '/pricing';
        }
        setLoading(false);
        return;
      }
      const reader = res.body!.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const text = decoder.decode(value);
        const lines = text.split('\n').filter(l => l.startsWith('data: '));
        for (const line of lines) {
          const data = JSON.parse(line.replace('data: ', ''));
          console.log("SSE data:", data);
          if (data.rate_limited) setRateLimitModal(true);
          setResults(prev => prev.map(r =>
            r.keyword === data.keyword ? { ...data, status: 'done' } : r
          ));
        }
      }
      await loadSavedKeywords(selectedProduct.id);
    } catch { setError('조회 실패'); }
    finally { setLoading(false); }
  };

  const handleCheck = () => doCheck(keywords);
  const handleRefreshAll = () => {
    const allKeywords = savedKeywords.map(sk => sk.keyword);
    doCheck(allKeywords, false);
  };
  const handleRefreshOne = (keyword: string) => doCheck([keyword], true);

  const handleDeleteKeyword = async (keywordId: number) => {
    if (!window.confirm('이 키워드 데이터를 삭제할까요?')) return;
    try {
      await client.delete(`/shopping/keyword/${keywordId}`);
      setResults(prev => prev.filter(r => r.keyword_id !== keywordId));
      setKeywords(prev => prev.map((k, i) => results[i]?.keyword_id === keywordId ? '' : k));
    } catch { alert('삭제 실패'); }
  };

  const s = {
    card: { background: '#161B27', border: '0.5px solid #2A3045', borderRadius: 8, padding: 20, marginBottom: 12 } as any,
    label: { display: 'block', color: '#8892A4', fontSize: 12, marginBottom: 6 } as any,
    input: { width: '100%', background: '#0F1117', border: '0.5px solid #2A3045', borderRadius: 8, padding: '10px 12px', color: '#E8ECF4', fontSize: 14, boxSizing: 'border-box', marginBottom: 12 } as any,
    btn: { background: '#00D4AA', border: 'none', borderRadius: 8, padding: '10px 24px', color: '#0A1F18', fontSize: 14, fontWeight: 700, cursor: 'pointer' } as any,
    btnGhost: { background: 'none', border: '0.5px solid #2A3045', borderRadius: 8, padding: '8px 16px', color: '#8892A4', fontSize: 13, cursor: 'pointer', marginRight: 8 } as any,
    btnBlue: { background: '#1A3A5C', border: 'none', borderRadius: 8, padding: '6px 14px', color: '#378ADD', fontSize: 12, fontWeight: 600, cursor: 'pointer' } as any,
    btnSmall: { background: '#1A2A1A', border: '0.5px solid #2A4A2A', borderRadius: 6, padding: '4px 10px', color: '#00D4AA', fontSize: 11, cursor: 'pointer' } as any,
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div style={{ background: '#1E2535', border: '0.5px solid #2A3045', borderRadius: 6, padding: '8px 12px' }}>
          <div style={{ color: '#8892A4', fontSize: 11 }}>{label}</div>
          <div style={{ color: '#00D4AA', fontSize: 14, fontWeight: 600 }}>{payload[0].value}위</div>
        </div>
      );
    }
    return null;
  };

  const KeywordCard = ({ keyword, keyword_id, history, currentRank, currentPage, status, rateLimited }: any) => {
    const displayRank = currentRank !== undefined ? currentRank : (history?.length > 0 ? history[history.length - 1].rank : null);
    const displayPage = currentPage !== undefined ? currentPage : null;

    return (
      <div style={{ ...s.card }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600 }}>{keyword}</div>
            {rateLimited && <span style={{ color: '#8892A4', fontSize: 11, background: '#1A1A2A', padding: '2px 6px', borderRadius: 4 }}>1시간 제한</span>}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {status !== 'loading' && (
              <>
                <button style={s.btnSmall} onClick={() => rateLimited ? setRateLimitModal(true) : handleRefreshOne(keyword)} disabled={loading}>갱신</button>
                <button style={{ background: 'none', border: '0.5px solid #4A2A2A', borderRadius: 6, padding: '4px 10px', color: '#F56565', fontSize: 11, cursor: 'pointer' }}
                  onClick={() => handleDeleteKeyword(keyword_id)}>삭제</button>
              </>
            )}
            {status === 'loading' ? (
              <div style={{ color: '#8892A4', fontSize: 13 }}>조회 중...</div>
            ) : displayRank ? (
              <div style={{ display: 'flex', gap: 8 }}>
                {displayPage && (
                  <div style={{ background: '#0F2A22', borderRadius: 6, padding: '6px 12px', textAlign: 'center' }}>
                    <div style={{ fontSize: 10, color: '#4A5568' }}>페이지</div>
                    <div style={{ fontSize: 16, fontWeight: 600, color: '#00D4AA' }}>{displayPage}p</div>
                  </div>
                )}
                <div style={{ background: '#0D1F35', borderRadius: 6, padding: '6px 12px', textAlign: 'center' }}>
                  <div style={{ fontSize: 10, color: '#4A5568' }}>순위</div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: '#378ADD' }}>{displayRank}위</div>
                </div>
              </div>
            ) : status === 'done' ? (
              <div style={{ color: '#F56565', fontSize: 13 }}>20페이지 내 노출 없음</div>
            ) : null}
          </div>
        </div>

        {history && history.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <ResponsiveContainer width="100%" height={100}>
              <LineChart data={history.filter((h: any) => h.rank)}>
                <XAxis dataKey="checked_at" tick={{ fill: '#4A5568', fontSize: 10 }} />
                <YAxis reversed domain={['dataMin - 2', 'dataMax + 2']} tick={{ fill: '#4A5568', fontSize: 10 }} />
                <Tooltip content={<CustomTooltip />} />
                <Line type="monotone" dataKey="rank" stroke="#00D4AA" strokeWidth={2} dot={{ fill: '#00D4AA', r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {history && history.length > 0 && (
          <div style={{ borderTop: '0.5px solid #2A3045', paddingTop: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
              <div style={{ color: '#4A5568', fontSize: 11, paddingBottom: 4 }}>날짜/시간</div>
              <div style={{ color: '#4A5568', fontSize: 11, paddingBottom: 4, textAlign: 'right' }}>순위</div>
              {[...history].reverse().slice(0, 7).map((h: any, j: number) => (
                <>
                  <div key={`d${j}`} style={{ color: '#8892A4', fontSize: 12, padding: '3px 0' }}>{h.checked_at}</div>
                  <div key={`r${j}`} style={{ color: h.rank ? '#00D4AA' : '#F56565', fontSize: 12, padding: '3px 0', textAlign: 'right', fontWeight: 600 }}>
                    {h.rank ? `${h.rank}위` : '노출없음'}
                  </div>
                </>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {rateLimitModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setRateLimitModal(false)}>
          <div style={{ background: '#161B27', border: '1px solid #F56565', borderRadius: 12, padding: '32px 40px', textAlign: 'center', maxWidth: 320 }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>⏱</div>
            <div style={{ color: '#E8ECF4', fontSize: 16, fontWeight: 700, marginBottom: 8 }}>1시간 제한</div>
            <div style={{ color: '#8892A4', fontSize: 13, marginBottom: 20, lineHeight: 1.6 }}>동일 상품은 1시간에 한 번만<br/>조회할 수 있습니다.</div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}><button style={{ background: 'none', border: '1px solid #2A3045', borderRadius: 8, padding: '8px 20px', color: '#8892A4', fontSize: 13, cursor: 'pointer' }} onClick={() => setRateLimitModal(false)}>닫기</button><button style={{ background: '#00D4AA', border: 'none', borderRadius: 8, padding: '8px 20px', color: '#0A1F18', fontWeight: 700, fontSize: 13, cursor: 'pointer' }} onClick={() => setRateLimitModal(false)}>플랜 업그레이드</button></div>
          </div>
        </div>
      )}
    <Layout>
      <div>
        {view === 'list' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div>
                <h2 style={{ color: '#E8ECF4', fontSize: 16, fontWeight: 500, margin: '0 0 4px' }}>쇼핑 순위 조회</h2>
                <p style={{ color: '#8892A4', fontSize: 12, margin: 0 }}>상품을 등록하고 키워드별 순위를 추적하세요</p>
              </div>
              <button style={s.btn} onClick={() => setView('add')}>+ 상품 등록</button>
            </div>
            {products.length === 0 ? (
              <div style={{ ...s.card, textAlign: 'center', padding: 40 }}>
                <div style={{ color: '#8892A4', fontSize: 14, marginBottom: 12 }}>등록된 상품이 없어요</div>
                <button style={s.btn} onClick={() => setView('add')}>첫 상품 등록하기</button>
              </div>
            ) : (
              products.map(p => (
                <div key={p.id} style={s.card}>
                  <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                    {p.thumbnail ? (
                      <img src={p.thumbnail} alt={p.name} referrerPolicy="no-referrer"
                        style={{ width: 80, height: 80, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
                    ) : (
                      <div style={{ width: 80, height: 80, borderRadius: 8, background: '#0F1117', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span style={{ color: '#2A3045', fontSize: 24 }}>🛍</span>
                      </div>
                    )}
                    <div style={{ flex: 1 }}>
                      <div style={{ color: '#E8ECF4', fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{p.name}</div>
                      {p.price > 0 && <div style={{ color: '#00D4AA', fontSize: 14, fontWeight: 700, marginBottom: 4 }}>{p.price.toLocaleString()}원</div>}
                      {p.category && <div style={{ color: '#4A5568', fontSize: 11, marginBottom: 8 }}>{p.category}</div>}
                      <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                        <a href={p.url} target="_blank" rel="noreferrer"
                          style={{ ...s.btnGhost, textDecoration: 'none', fontSize: 12, padding: '6px 14px' }}>
                          바로가기
                        </a>
                        <button style={{ ...s.btnBlue, fontSize: 12, padding: '6px 14px' }} onClick={() => handleSelectProduct(p)}>분석하기</button>
                        <button style={{ background: p.auto_track ? '#0F2A22' : '#1A2A1A', border: `0.5px solid ${p.auto_track ? '#2A4A2A' : '#2A3A2A'}`, borderRadius: 8, padding: '6px 14px', color: p.auto_track ? '#00D4AA' : '#8892A4', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                          onClick={() => handleToggleAutoTrack(p.id)}>
                          {p.auto_track ? '🟢 자동추적 ON' : '자동순위추적'}
                        </button>
                        <button style={{ background: 'none', border: '0.5px solid #4A2A2A', borderRadius: 8, padding: '6px 14px', color: '#F56565', fontSize: 12, cursor: 'pointer' }}
                          onClick={() => handleDeleteProduct(p.id)}>
                          삭제
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </>
        )}

        {view === 'add' && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <button style={s.btnGhost} onClick={() => { setView('list'); setError(''); }}>← 뒤로</button>
              <h2 style={{ color: '#E8ECF4', fontSize: 16, fontWeight: 500, margin: 0 }}>상품 등록</h2>
            </div>
            <div style={s.card}>
              <label style={s.label}>스마트스토어 상품 URL</label>
              <input style={s.input} value={newUrl} onChange={e => setNewUrl(e.target.value)}
                placeholder="https://smartstore.naver.com/스토어명/products/상품번호" />
              <p style={{ color: '#8892A4', fontSize: 12, marginBottom: 16 }}>URL만 입력하면 상품명, 가격, 카테고리, 썸네일을 자동으로 가져와요</p>
              {error && <p style={{ color: '#F56565', fontSize: 13, marginBottom: 12 }}>{error}</p>}
              <button style={s.btn} onClick={handleAddProduct} disabled={adding}>
                {adding ? '정보 수집 중... (30초 소요)' : '등록하기'}
              </button>
            </div>
          </>
        )}

        {view === 'detail' && selectedProduct && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <button style={s.btnGhost} onClick={() => { setView('list'); setResults([]); setSavedKeywords([]); }}>← 뒤로</button>
              <div>
                <h2 style={{ color: '#E8ECF4', fontSize: 16, fontWeight: 500, margin: '0 0 2px' }}>{selectedProduct.name}</h2>
                <div style={{ color: '#8892A4', fontSize: 12 }}>{selectedProduct.store_name}</div>
              </div>
            </div>

            <div style={s.card}>
              <label style={s.label}>검색 키워드 (최대 10개)</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
                {keywords.map((kw, i) => (
                  <input key={i} style={{ ...s.input, margin: 0 }} value={kw}
                    onChange={e => { const u = [...keywords]; u[i] = e.target.value; setKeywords(u); }}
                    placeholder={`키워드 ${i + 1}`} />
                ))}
              </div>
              {keywords.length < 10 && (
                <button style={{ ...s.btnGhost, marginBottom: 12 }} onClick={() => setKeywords([...keywords, ''])}>+ 키워드 추가</button>
              )}
              {error && <p style={{ color: '#F56565', fontSize: 13, marginBottom: 12 }}>{error}</p>}
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={s.btn} onClick={handleCheck} disabled={loading}>
                  {loading ? '조회 중...' : '순위 조회'}
                </button>
                {savedKeywords.length > 0 && (
                  <button style={{ ...s.btnGhost, background: '#1A2A3A', color: '#378ADD', border: '0.5px solid #2A3A4A' }}
                    onClick={handleRefreshAll} disabled={loading}>
                    전체 갱신
                  </button>
                )}
              </div>
            </div>

            {results.map((r, i) => (
              <KeywordCard key={`result-${i}`} keyword={r.keyword}
                history={savedKeywords.find(sk => sk.keyword === r.keyword)?.history || []}
                currentRank={r.total_rank} currentPage={r.page} status={r.status}
                rateLimited={r.rate_limited} />
            ))}

            {savedKeywords
              .filter(sk => !results.find(r => r.keyword === sk.keyword))
              .map((sk, i) => (
                <KeywordCard key={`saved-${i}`} keyword={sk.keyword} history={sk.history} status="saved" />
              ))}
          </>
        )}
      </div>
    </Layout>
    </>
  );
}
